import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import Collection from 'assets/components/data/Collection';
import ThemeCollectionList from 'assets/components/data/themes/Theme.Collection.List';
import DataMap from 'assets/components/dataMap/DataMap';
import SortButton from 'assets/components/filters/sortButton/SortButton';
import FormatValue from 'assets/components/formatValue/FormatValue';
import Icon from 'assets/components/icon/Icon';
import Page from 'assets/components/page/Page';
import Confirm from 'assets/components/page/templates/Confirm';
import ThemeConfirmDefault from 'assets/components/page/templates/themes/Theme.Confirm.Default';
import ThemePageError from 'assets/components/page/themes/Theme.Page.Error';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import ThemeTagRounded from 'assets/components/tag/themes/Theme.Tag.Rounded';
import TextBox, { textBoxStyles } from 'assets/components/textBox/TextBox';
import useApi from 'assets/hooks/api/useApi';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import Theme from 'assets/themes/Theme.Common';
import { date, defaultDateTimeFormat, defaultServerDateTimeFormat } from 'assets/utils/data/Date';
import { cleanValues } from 'assets/utils/data/Object';
import { combineStrings } from 'assets/utils/data/String';
import dayjs from 'dayjs';
import { entries, first, groupBy, sortBy, sumBy } from 'lodash';
import Claim from 'models/claims/claim/Model.Claim';
import ClaimJob from 'models/claims/claimJob/Model.ClaimJob';
import ClaimJobComment from 'models/claims/claimJobComment/ClaimJobComment';
import useClaimRedirect from 'module/claims/hooks/useClaimRedirect';
import React, { useState } from 'react';
import { isDesktop } from 'react-device-detect';
import useClaim, { useClaimProvider } from '../Claim.Hooks';
import ClaimFilters from './Claim.Filters';

function ClaimJobList({ claim }: { claim: Claim }) {
  const claimJobId = claim.claimJobs?.map((it) => it.id);
  const { lang } = getLocales();
  const { permissions } = useAuthStore();
  const { isClaimAdmin } = useClaim();

  const canViewClaimJobComments =
    isClaimAdmin ||
    permissions?.CREATE_CLAIMJOBCOMMENT ||
    permissions?.VIEW_CLAIMJOBCOMMENT ||
    permissions?.EDIT_CLAIMJOBCOMMENT ||
    permissions?.DELETE_CLAIMJOBCOMMENT;

  const latestCommentApi = useApi(
    {
      action: ClaimJobComment.list,
      body: { claimJobId, latestPerJob: true },
      wait: !claimJobId || !canViewClaimJobComments,
      keepResponse: true,
    },
    [JSON.stringify(sortBy(claimJobId)), canViewClaimJobComments]
  );
  const jobsApi = useApi(
    {
      action: ClaimJob.list,
      body: { claimId: claim.id },
      wait: !claim.id,
      keepResponse: true,
    },
    [claim.id]
  );
  return (
    <>
      <tr>
        <td>{lang.jobNumber}</td>
        <td>{lang.claimType}</td>
        <td>{lang.decidedDate}</td>
        <td>{lang.paymentDate}</td>
        <td>{lang.lastModified}</td>
        <td>{lang.status}</td>
        <td>{lang.amountClaimed}</td>
        <td></td>
      </tr>
      <DataMap
        data={sortBy(jobsApi.payload?.data ?? [], (it) => it.jobNumber)}
        render={({ data: claimJob }) => {
          const claimJobAggregates = ClaimJob.aggregates(claimJob);
          const groupedByCurrency = entries(groupBy(claimJobAggregates, (i) => i.currency));

          return (
            <React.Fragment key={claimJob.id}>
              <tr>
                <td>
                  <b>
                    {claim.number}-J{claimJob.jobNumber}
                  </b>
                </td>
                <td>{claimJob.claimType.name}</td>
                <td>
                  {claimJob?.decidedDateUtc
                    ? date(claimJob?.decidedDateUtc, defaultServerDateTimeFormat).format(defaultDateTimeFormat)
                    : 'N/A'}
                </td>
                <td>
                  {claimJob.paymentDateUtc
                    ? date(claimJob?.paymentDateUtc, defaultServerDateTimeFormat).format(defaultDateTimeFormat)
                    : 'N/A'}
                </td>
                <td>
                  {claimJob.modifiedAtUtc
                    ? date(claimJob?.modifiedAtUtc, defaultServerDateTimeFormat).format(defaultDateTimeFormat)
                    : 'N/A'}
                </td>
                <td style={{ fontSize: Theme.FontSize.S }}>
                  <b>{claimJob.statusCode?.replace(/_/g, ' - ') || lang.noStatusDefined}</b>
                </td>
                <td>
                  <DataMap
                    data={groupedByCurrency}
                    render={({ data: [currency, items] }) => (
                      <div key={currency}>
                        <FormatValue value={`${sumBy(items, (it) => it.total).toFixed(2)}${currency}`} />
                      </div>
                    )}
                  />
                </td>
                <td></td>
              </tr>
              <tr>
                <td colSpan={8}>
                  <b>{lang.latestNote}: </b>
                  <span>
                    {latestCommentApi?.isExecuting
                      ? lang.loading
                      : first(latestCommentApi?.payload?.data?.filter((c) => c.claimJobId === claimJob.id))?.comment ??
                        'N/A'}
                  </span>
                  <br />
                  <b>{lang.customerComplaint}: </b>
                  <span>{claimJob.customerComplaint ?? 'N/A'}</span>
                  <br />
                  <b>{lang.causeOfFailure}: </b>
                  <span>{claimJob.cause ?? 'N/A'}</span>
                  <br />
                  <b>{lang.correction}: </b> <span>{claimJob.correction ?? 'N/A'}</span>
                </td>
              </tr>
            </React.Fragment>
          );
        }}
      />
    </>
  );
}

export default function ClaimList() {
  const { lang } = getLocales();
  const { listApi, pageRouter, statusInfo, emailTemplates, productsApi, hasAnyPermission, automatedClaim } =
    useClaimProvider();
  const { redirectAndTakeOwnership, setRedirectInfo, redirectInfo } = useClaimRedirect({ emailTemplates, statusInfo });
  const [expanedClaimId, setExpandedClaimId] = useState<number>();

  function handleSort(newFilters: Api.IDefaultListRequest) {
    pageRouter.updateQuery({
      ...pageRouter.query,
      list: cleanValues({
        ...pageRouter.query?.list,
        ...newFilters,
      }),
    });
  }

  return !listApi.payload && !listApi.isExecuting && listApi.apiCounter > 0 ? (
    <Page
      class={ThemePageError}
      sidebar={<Icon class="fas-clipboard-list" />}
      header={lang.claims}
      render={lang.noInfo}
    />
  ) : (
    <>
      {redirectInfo?.claim && !redirectInfo?.claim?.owner && (
        <Confirm
          onConfirm={() => redirectAndTakeOwnership(redirectInfo)}
          onClose={() => setRedirectInfo(null)}
          actionName={lang.takeOwnershipOfTheClaim}
          itemName={redirectInfo?.claim?.id?.toString()}
          confirmationType="nextButton"
          subcomponents={ThemeConfirmDefault}
        />
      )}
      <Collection class={ThemeCollectionList}>
        {isDesktop && <ClaimFilters />}
        <table className={ThemeTableSimple}>
          <thead>
            <tr>
              <th>
                {lang.claim}
                <SortButton orderBy="number" apiBody={listApi.body} onSort={handleSort} />
              </th>
              <th>{lang.info}</th>
              <th>
                {lang.incidentLocalDate}
                <SortButton orderBy="incidentLocalDate" apiBody={listApi.body} onSort={handleSort} />
              </th>
              <th>
                {lang.createdAt}
                <SortButton orderBy="createdAtUtc" apiBody={listApi.body} onSort={handleSort} />
              </th>
              <th>
                {lang.lastModified}
                <SortButton orderBy="modifiedAtUtc" apiBody={listApi.body} onSort={handleSort} />
              </th>
              <th>
                {lang.status}
                <SortButton orderBy="statusCode" apiBody={listApi.body} onSort={handleSort} />
              </th>
              <th>
                {lang.owner}
                <SortButton
                  orderBy="owner.firstName"
                  thenBy="owner.lastName"
                  apiBody={listApi.body}
                  onSort={handleSort}
                />
              </th>
              <th>{lang.actions}</th>
            </tr>
          </thead>
          <tbody>
            <DataMap
              data={listApi.payload?.data}
              render={({ data: claim }) => {
                const isNew = pageRouter.query.newClaimId === claim.id;
                return (
                  <React.Fragment key={claim.id}>
                    <tr style={{ borderTop: `2px solid ${Theme.Color.shadow}` }}>
                      <td>
                        <div style={{ display: 'flex', gap: Theme.Size.XS, alignItems: 'center' }}>
                          {automatedClaim?.[claim.id] && (
                            <Box
                              class={ThemeTagRounded}
                              style={{
                                backgroundColor: Theme.Color.success,
                                fontSize: Theme.FontSize.S,
                              }}
                            >
                              A
                            </Box>
                          )}
                          <Box
                            class={ThemeTagRounded}
                            style={{
                              backgroundColor: claim.claimJobCount ? null : Theme.Color.error,
                              fontSize: Theme.FontSize.S,
                            }}
                          >
                            {claim.claimJobCount > 1
                              ? `${claim.claimJobCount} ${lang.jobs}`
                              : claim.claimJobCount === 1
                              ? `1 ${lang.job}`
                              : lang.noJobs}
                          </Box>
                        </div>
                        <TextBox class={textBoxStyles.layout.title} style={{ fontWeight: 'bold' }}>
                          {claim.number ??
                            combineStrings(
                              '-',
                              productsApi?.payload?.data?.find(
                                (it) => it.id === (claim?.contract?.product?.id || claim?.contract?.productId)
                              )?.category?.code,
                              claim.id.toString()
                            )}
                        </TextBox>
                        <br />
                        <TextBox class={textBoxStyles.layout.infoLabel}>
                          {claim.contract?.product?.name ?? lang.unknownProduct}
                        </TextBox>
                      </td>
                      <td>
                        {lang.contact}: <b>{claim.createdByPayee?.name || 'N/A'}</b>
                        <br />
                        {lang.client}:{' '}
                        <b>
                          {combineStrings(' ', claim.contract?.client?.firstName, claim.contract?.client?.lastName) ??
                            'N/A'}
                        </b>
                      </td>
                      <td>
                        {claim?.incidentLocalDate
                          ? dayjs(claim?.incidentLocalDate, defaultServerDateTimeFormat).format(defaultDateTimeFormat)
                          : 'N/A'}
                      </td>
                      <td>
                        {date(claim?.createdAtUtc, defaultServerDateTimeFormat).format(defaultDateTimeFormat)}
                        {isNew && (
                          <span
                            style={{
                              backgroundColor: Theme.Color.success,
                              fontSize: Theme.FontSize.S,
                              fontWeight: 'bold',
                              borderRadius: '100px',
                              color: 'white',
                              padding: '3px 6px',
                              marginTop: Theme.Size.XS,
                            }}
                          >
                            {lang.newlyCreated.toUpperCase()}
                          </span>
                        )}
                      </td>
                      <td>{date(claim?.modifiedAtUtc, defaultServerDateTimeFormat).format(defaultDateTimeFormat)}</td>
                      <td style={{ fontSize: Theme.FontSize.S }}>
                        <b>{claim.statusCode.replace(/_/g, ' - ') || lang.noStatusDefined}</b>
                      </td>
                      <td>{claim.owner ? `${claim.owner?.firstName} ${claim.owner?.lastName}` : 'N / A'}</td>
                      <td>
                        <Button
                          onClick={!claim.owner ? setRedirectInfo : redirectAndTakeOwnership}
                          data={{ claim }}
                          class={ThemeButtonCircle}
                          disabled={!hasAnyPermission}
                          media="fas-folder-open"
                          htmlElementProps={{
                            title: `${lang.open} ${lang.claim}`,
                          }}
                          render={lang.open}
                        />
                        <Button
                          onClick={setExpandedClaimId}
                          data={expanedClaimId === claim.id ? null : claim.id}
                          class={ThemeButtonCircle}
                          disabled={!hasAnyPermission}
                          media={<Icon class={expanedClaimId === claim.id ? 'fas-chevron-up' : 'fas-chevron-down'} />}
                          htmlElementProps={{
                            title: expanedClaimId === claim.id ? lang.viewLess : lang.viewMore,
                          }}
                          render={expanedClaimId === claim.id ? lang.viewLess : lang.viewMore}
                        />
                      </td>
                    </tr>
                    {expanedClaimId === claim.id && <ClaimJobList claim={claim} />}
                  </React.Fragment>
                );
              }}
            />
          </tbody>
        </table>
      </Collection>
    </>
  );
}
